import React from 'react';

import { useTranslation } from '@modernloop/i18n';
import { MagicIcon } from '@modernloop/shared/icons';
import { Stack, Typography } from '@mui/material';

import { DynamicInterviewMeetingHost } from 'src/generated/mloop-graphql';

type Props = {
  /**
   * The dynamic host type to display
   */
  dynamicHost: DynamicInterviewMeetingHost;
};

/**
 * VideoMeetingHostDynamicHost component
 *
 * Displays dynamic host information for a video meeting.
 * Shows a magic icon with the appropriate label based on the dynamic host type.
 *
 * @param dynamicHost - The type of dynamic host (Interviewers, Scheduler, etc.)
 * @returns A Stack component with a magic icon and the host type label
 */
const VideoMeetingHostDynamicHost: React.FC<Props> = ({ dynamicHost }) => {
  const { t } = useTranslation();
  const getLabel = (): string => {
    switch (dynamicHost) {
      case DynamicInterviewMeetingHost.Interviewers:
        return t('hiring-dashboard-common:common.dynamicHost.interviewers');
      case DynamicInterviewMeetingHost.Scheduler:
        return t('hiring-dashboard-common:common.dynamicHost.scheduler');
      case DynamicInterviewMeetingHost.CustomMeetingHost:
        return t('hiring-dashboard-common:common.dynamicHost.customMeetingHost');
      case DynamicInterviewMeetingHost.CustomZoomHost:
        return t('hiring-dashboard-common:common.dynamicHost.customZoomHost');
      default:
        return t('hiring-dashboard-common:common.unknown');
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <MagicIcon color="primary" />
      <Typography noWrap>{getLabel()}</Typography>
    </Stack>
  );
};

export default VideoMeetingHostDynamicHost;
